import { useContext, useRef, useEffect, useMemo, useCallback, useState } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import styled from 'styled-components'
import debounce from 'lodash/debounce'
import PropTypes from 'prop-types'

import { api } from '@h2/api'
import { Flex } from '@h2/components/layout/flex'
import { Box } from '@h2/components/layout/box'
import { PB, PB97T, PB40T } from '@h2/components/colors'
import { base, inputReset, lists } from '@h2/components/mixins'

import { Context } from '../context'
import { Link } from '../../link'
import { Icon } from '../search/icon'
import { useNavigator } from '../../hooks/useNavigator'

const SearchIcon = styled(FontAwesomeIcon)`
  position: absolute;
  top: 15px;
  left: 15px;
`
const CancelIcon = styled(FontAwesomeIcon)`
  && {
    position: absolute;
    top: 10px;
    right: 10px;
    box-sizing: content-box;
    padding: 5px;
  }
`
const Wrap = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  bottom: ${({ navHeight }) => navHeight};
  width: 100%;
  background-color: ${PB97T};
  z-index: 1;
`
const FlexWrap = styled(Flex)`
  position: relative;
  height: 100%;

  form {
    label {
      display: block;
    }
  }
`
const BoxResults = styled(Box)`
  padding: 30px 20px;
  height: 100%;
  overflow-y: auto;
`
const Results = styled.ul`
  ${lists};

  > li {
    display: flex;
    align-items: center;
    margin-bottom: 20px;

    a {
      margin-left: 10px;
    }

    span {
      border-radius: 50%;
      background-color: white;
      border: 1px solid white;
      padding: 3px;
    }

    &:last-child {
      margin-bottom: 0;
    }
  }
`
const Input = styled.input.attrs({ type: 'search' })`
  ${inputReset};
  ${base};
  width: 100%;
  border: none;
  font-size: 18px;
  padding: 15px 40px 15px 45px;
  color: ${PB40T};

  &:focus-visible {
    outline: 0;
  }

  &:focus {
    outline: 0;
  }
`
const isValidQuery = string => /\w{3}/.test(string ?? '')
const Search = ({ onClickClose }) => {
  const ref = useRef()
  const navigator = useNavigator()
  const { navHeight } = useContext(Context)
  const [results, setResults] = useState([])
  const handleOnInput = useMemo(() => {
    return debounce(
      async ({ target }) => {
        if (isValidQuery(target.value)) {
          const encoded = encodeURIComponent(target.value.trim())
          const apiResults = await api.fetch(`/search/suggest/${encoded}`, {
            version: 'v1'
          })

          setResults(apiResults)
        } else {
          setResults([])
        }
      },
      250,
      { leading: true }
    )
  }, [setResults])
  const handleOnSubmit = useCallback(
    evt => {
      evt.preventDefault()
      navigator(`/search?q=${ref.current.value}`)
    },
    [navigator]
  )

  useEffect(() => {
    if (ref.current) {
      ref.current.focus()
    }
  }, [])

  return (
    <Wrap navHeight={navHeight}>
      <FlexWrap direction="column">
        <SearchIcon icon="search" size="2x" color={PB40T} />
        <CancelIcon
          icon="times"
          size="2x"
          color={PB}
          onClick={evt => {
            evt.stopPropagation()
            onClickClose()
          }}
        />
        <Box>
          <form onSubmit={handleOnSubmit}>
            <label aria-label="Search">
              <Input ref={ref} onInput={handleOnInput} />
            </label>
          </form>
        </Box>
        <BoxResults>
          <Results>
            {results.map(({ label, value, type }) => (
              <li key={label}>
                <Icon type={type} size="tiny" />
                <Link href={value}>{label}</Link>
              </li>
            ))}
          </Results>
        </BoxResults>
      </FlexWrap>
    </Wrap>
  )
}

Search.propTypes = {
  onClickClose: PropTypes.func.isRequired
}

export { Search, Input }
